import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Code as CodeIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Psychology as AiIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { detectChartType, getChartDescription } from '../../utils/chartDetection';
import { downloadAsFile } from '../../utils/downloadAsFile';
import QueryResultChart from './QueryResultChart';

const NaturalLanguageQuery = ({ open, onClose }) => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(''); // Descriptive loading message
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showSQL, setShowSQL] = useState(true);
  const [chartConfig, setChartConfig] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'chart' or 'table' - defaults to table
  const [sqlExplanation, setSqlExplanation] = useState('');
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);


  const handleSubmit = async () => {
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    if (question.length > 500) {
      setError('Question too long (max 500 characters)');
      return;
    }

    setLoading(true);
    setLoadingStatus('Analyzing your question...');
    setError('');
    setResult(null);
    setSqlExplanation('');
    setShowExplanation(false);

    try {
      const token = localStorage.getItem('token');

      // Simulate brief "analyzing" phase for UX
      await new Promise(resolve => setTimeout(resolve, 300));
      setLoadingStatus('Generating SQL query...');

      const response = await axios.post(
        '/api/nlquery',
        { question: question.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setLoadingStatus('Preparing results...');

      // Check if query returned no results
      if (response.data.success && response.data.rowCount === 0) {
        setResult(response.data);
        setChartConfig(null);
        setLoadingStatus('');
        toast.info('Query executed successfully, but no matching results found');
        return;
      }

      setResult(response.data);

      // Detect if results can be visualized
      if (response.data.data && response.data.data.length > 0) {
        const detected = detectChartType(response.data);
        setChartConfig(detected);

        // Check if user explicitly asked for a visualization
        const questionLower = question.toLowerCase();
        const visualizationKeywords = [
          'visualize', 'visualization', 'chart', 'graph', 'plot',
          'show me a chart', 'show me a graph', 'create a visualization',
          'bar chart', 'pie chart', 'line chart'
        ];
        const askedForVisualization = visualizationKeywords.some(keyword =>
          questionLower.includes(keyword)
        );

        // If they asked for visualization and we detected one, show chart
        // Otherwise default to table view
        if (detected && askedForVisualization) {
          setLoadingStatus('Generating visualization...');
          await new Promise(resolve => setTimeout(resolve, 200)); // Brief pause for UX
          setViewMode('chart');
        } else {
          setViewMode('table'); // Default to table view
        }
      }

      setLoadingStatus('');
      toast.success(`Query successful! ${response.data.rowCount} results found`);
    } catch (err) {
      console.error('NL Query error:', err);
      setLoadingStatus('');

      // Enhanced error handling with specific messages
      if (err.response?.status === 429) {
        setError('⏱️ Rate limit exceeded. You can make 10 queries per hour. Please try again later.');
        toast.error('Rate limit exceeded');
      } else if (err.response?.status === 400) {
        // Validation or SQL generation errors
        const errorMsg = err.response.data?.message || err.response.data?.error;
        if (errorMsg && errorMsg.includes('validation')) {
          setError('🔍 I couldn\'t generate a safe query for that question. Try rephrasing or being more specific.');
        } else if (errorMsg && errorMsg.includes('NO_MATCHING_DATA')) {
          setError('❌ No matching data found in the database for your query. Try a different question.');
        } else {
          setError(`⚠️ ${errorMsg || 'Could not process your question. Try rephrasing it differently.'}`);
        }
        toast.error('Query failed');
      } else if (err.response?.status === 500) {
        setError('🤖 AI service error. The system had trouble understanding your question. Try rephrasing or asking something simpler.');
        toast.error('AI service error');
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('⏱️ Query timeout. Your question might be too complex. Try being more specific or adding filters.');
        toast.error('Query timeout');
      } else if (!err.response) {
        setError('🔌 Connection error. Please check your network connection and try again.');
        toast.error('Connection error');
      } else {
        setError('⚠️ An unexpected error occurred. Please try again or rephrase your question.');
        toast.error('Query failed');
      }
    } finally {
      setLoading(false);
      setLoadingStatus('');
    }
  };

  const handleClear = () => {
    setQuestion('');
    setResult(null);
    setError('');
    setChartConfig(null);
    setViewMode('table');
    setLoadingStatus('');
    setSqlExplanation('');
    setShowExplanation(false);
  };

  const handleClose = () => {
    handleClear();
    onClose();
  };

  const handleExplainQuery = () => {
    if (!result || !result.sql) return;

    setLoadingExplanation(true);

    // Generate a simple plain-English explanation of the SQL
    const sql = result.sql.toLowerCase();
    let explanation = '';

    // Determine query type
    if (sql.includes('count(')) {
      explanation = 'This query **counts** records ';
    } else if (sql.includes('select')) {
      explanation = 'This query **retrieves** records ';
    }

    // Identify main table
    const fromMatch = sql.match(/from\s+(\w+)/);
    if (fromMatch) {
      const table = fromMatch[1];
      explanation += `from the **${table}** table`;
    }

    // Identify joins
    if (sql.includes('join')) {
      const joinMatches = sql.match(/join\s+(\w+)/g);
      if (joinMatches) {
        const tables = joinMatches.map(j => j.replace('join ', ''));
        explanation += `, joined with **${tables.join('**, **')}**`;
      }
    }

    // Identify filtering
    if (sql.includes('where')) {
      explanation += '. It **filters** the results';

      if (sql.includes('ilike')) {
        explanation += ' by matching text patterns';
      }
      if (sql.includes('is not null')) {
        explanation += ' to exclude missing values';
      }
      if (sql.includes('<') || sql.includes('>')) {
        explanation += ' based on numeric or date comparisons';
      }
    }

    // Identify grouping
    if (sql.includes('group by')) {
      const groupMatch = sql.match(/group\s+by\s+([a-z_\.]+)/);
      if (groupMatch) {
        explanation += `. Results are **grouped by ${groupMatch[1]}**`;
      }
    }

    // Identify sorting
    if (sql.includes('order by')) {
      explanation += '. Results are **sorted** by the specified column';
      if (sql.includes('desc')) {
        explanation += ' in descending order';
      }
    }

    // Identify limits
    if (sql.includes('limit')) {
      const limitMatch = sql.match(/limit\s+(\d+)/);
      if (limitMatch) {
        explanation += `. Limited to **${limitMatch[1]} results**`;
      }
    }

    explanation += '.';

    setSqlExplanation(explanation);
    setShowExplanation(true);
    setLoadingExplanation(false);
  };

  const exportToCSV = () => {
    if (!result || !result.data || result.data.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const columns = Object.keys(result.data[0]);
      const timestamp = new Date().toISOString();

      // Build CSV content with metadata header
      let csvContent = `# Natural Language Query Export\n`;
      csvContent += `# Question: ${result.question}\n`;
      csvContent += `# Timestamp: ${timestamp}\n`;
      csvContent += `# Records: ${result.rowCount}\n`;
      csvContent += `# SQL: ${result.sql.replace(/\n/g, ' ')}\n`;
      csvContent += `\n`;

      // Add column headers
      csvContent += columns.map(col => `"${col}"`).join(',') + '\n';

      // Add data rows
      result.data.forEach(row => {
        const values = columns.map(col => {
          const value = row[col];
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvContent += values.join(',') + '\n';
      });

      const filename = `nlquery_results_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
      downloadAsFile(csvContent, filename, 'text/csv;charset=utf-8;');
      toast.success('CSV exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export CSV');
    }
  };

  const exportToExcel = () => {
    if (!result || !result.data || result.data.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const columns = Object.keys(result.data[0]);
      const timestamp = new Date().toISOString();

      // Build TSV content (Tab-separated, works well with Excel)
      let tsvContent = `Natural Language Query Export\t\t\t\n`;
      tsvContent += `Question:\t${result.question}\t\t\n`;
      tsvContent += `Timestamp:\t${timestamp}\t\t\n`;
      tsvContent += `Records:\t${result.rowCount}\t\t\n`;
      tsvContent += `SQL:\t${result.sql.replace(/\n/g, ' ')}\t\t\n`;
      tsvContent += `\t\t\t\n`;

      // Add column headers
      tsvContent += columns.join('\t') + '\n';

      // Add data rows
      result.data.forEach(row => {
        const values = columns.map(col => {
          const value = row[col];
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return JSON.stringify(value);
          return String(value);
        });
        tsvContent += values.join('\t') + '\n';
      });

      const filename = `nlquery_results_${new Date().toISOString().replace(/[:.]/g, '-')}.xls`;
      downloadAsFile(tsvContent, filename, 'application/vnd.ms-excel;charset=utf-8;');
      toast.success('Excel file exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export Excel file');
    }
  };

  const renderResultTable = () => {
    if (!result || !result.data || result.data.length === 0) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>No results found</strong>
          </Typography>
          <Typography variant="body2">
            Your query was executed successfully, but didn't match any records in the database.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Try:
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
            <li><Typography variant="body2">Using different search terms or filters</Typography></li>
            <li><Typography variant="body2">Broadening your criteria (remove specific dates, names, etc.)</Typography></li>
            <li><Typography variant="body2">Checking for typos in specimen IDs, project names, or disease names</Typography></li>
            <li><Typography variant="body2">Asking a more general question first to explore what data is available</Typography></li>
          </Box>
        </Alert>
      );
    }

    const columns = Object.keys(result.data[0]);

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 'bold', backgroundColor: 'background.paper' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {result.data.map((row, idx) => (
              <TableRow key={idx} hover>
                {columns.map((col) => (
                  <TableCell key={col}>
                    {row[col] !== null && row[col] !== undefined
                      ? typeof row[col] === 'object'
                        ? JSON.stringify(row[col])
                        : String(row[col])
                      : '-'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon color="primary" />
            <Typography variant="h6">Natural Language Query</Typography>
            <Chip label="PROTOTYPE" size="small" color="warning" />
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Ask analytical questions</strong> about your data in plain English.
              Best for counts, aggregations, and cross-table insights.
              Powered by Claude AI • Limited to 10 queries per hour per user.
            </Typography>
          </Alert>

          {/* Question Input */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Your Question
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g., Show me all specimens from project 25 collected last week"
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {question.length}/500 characters
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  disabled={loading || (!question && !result)}
                  size="small"
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !question.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  {loading ? 'Processing...' : 'Ask'}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Loading Status Display */}
          {loading && loadingStatus && (
            <Alert
              severity="info"
              icon={<CircularProgress size={20} />}
              sx={{ mb: 2, backgroundColor: 'primary.50' }}
            >
              <Typography variant="body2">
                <strong>{loadingStatus}</strong>
              </Typography>
            </Alert>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Results */}
          {result && (
            <Box>
              <Divider sx={{ my: 2 }} />

              {/* Interpretation Display */}
              {result.interpretation && (
                <Alert severity="info" icon={<AiIcon />} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Query Interpretation:</strong>
                  </Typography>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {result.interpretation}
                  </Typography>
                </Alert>
              )}

              {/* SQL Display (collapsible) */}
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => setShowSQL(!showSQL)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CodeIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2">
                      Generated SQL Query
                    </Typography>
                  </Box>
                  <IconButton size="small">
                    {showSQL ? <CollapseIcon /> : <ExpandIcon />}
                  </IconButton>
                </Box>
                <Collapse in={showSQL}>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: 'grey.50',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        overflow: 'auto'
                      }}
                    >
                      <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
                        {result.sql}
                      </Typography>
                      {result.params && result.params.length > 0 && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary">
                            Parameters: {JSON.stringify(result.params)}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </Collapse>
              </Paper>

              {/* Explain This Query Button & Explanation */}
              {!showExplanation ? (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<InfoIcon />}
                    onClick={handleExplainQuery}
                    disabled={loadingExplanation}
                    sx={{ textTransform: 'none' }}
                  >
                    {loadingExplanation ? 'Generating explanation...' : 'Explain This Query in Plain English'}
                  </Button>
                </Box>
              ) : (
                <Alert
                  severity="success"
                  icon={<InfoIcon />}
                  sx={{ mb: 2 }}
                  onClose={() => setShowExplanation(false)}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Plain English Explanation:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {sqlExplanation.split('**').map((part, idx) =>
                      idx % 2 === 0 ? part : <strong key={idx}>{part}</strong>
                    )}
                  </Typography>
                </Alert>
              )}

              {/* Results Summary and Controls */}
              <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label={`${result.rowCount} ${result.rowCount === 1 ? 'result' : 'results'} found`}
                    color="success"
                    icon={<AiIcon />}
                  />
                  {chartConfig && (
                    <Chip
                      label={getChartDescription(chartConfig)}
                      icon={<BarChartIcon />}
                      variant="outlined"
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {chartConfig && (
                    <Box sx={{ display: 'flex', gap: 0.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Button
                        size="small"
                        variant={viewMode === 'chart' ? 'contained' : 'text'}
                        startIcon={<BarChartIcon />}
                        onClick={() => setViewMode('chart')}
                        sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5 }}
                      >
                        Chart
                      </Button>
                      <Button
                        size="small"
                        variant={viewMode === 'table' ? 'contained' : 'text'}
                        startIcon={<TableChartIcon />}
                        onClick={() => setViewMode('table')}
                        sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5 }}
                      >
                        Table
                      </Button>
                    </Box>
                  )}

                  <Divider orientation="vertical" flexItem />

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={exportToCSV}
                    sx={{ textTransform: 'none' }}
                  >
                    CSV
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={exportToExcel}
                    sx={{ textTransform: 'none' }}
                  >
                    Excel
                  </Button>
                </Box>
              </Box>

              {/* Visualization or Table */}
              {chartConfig && viewMode === 'chart' ? (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <QueryResultChart chartConfig={chartConfig} />
                </Paper>
              ) : null}

              {/* Results Table - show if view mode is table or no chart config */}
              {viewMode === 'table' || !chartConfig ? renderResultTable() : null}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          Queries are read-only and validated for safety
        </Typography>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NaturalLanguageQuery;
